
// These icons are from http://design/icons, in the 'Schema' section.
// Their names are intentionally kept the same as on that page for easy reference.
import { assertNever } from '@tableau/data-structures';
import * as React from 'react';
import { SvgSchemaViewerBoolCalcDiscrete } from '../../../../gen-src/icons/SchemaViewerBoolCalcDiscrete';
import { SvgSchemaViewerBoolDiscrete } from '../../../../gen-src/icons/SchemaViewerBoolDiscrete';
import { SvgSchemaViewerDateCalcContinuous } from '../../../../gen-src/icons/SchemaViewerDateCalcContinuous';
import { SvgSchemaViewerDateCalcDiscrete } from '../../../../gen-src/icons/SchemaViewerDateCalcDiscrete';
import { SvgSchemaViewerDateContinuous } from '../../../../gen-src/icons/SchemaViewerDateContinuous';
import { SvgSchemaViewerDateDiscrete } from '../../../../gen-src/icons/SchemaViewerDateDiscrete';
import { SvgSchemaViewerDateTimeCalcContinuous } from '../../../../gen-src/icons/SchemaViewerDateTimeCalcContinuous';
import { SvgSchemaViewerDateTimeCalcDiscrete } from '../../../../gen-src/icons/SchemaViewerDateTimeCalcDiscrete';
import { SvgSchemaViewerDateTimeContinuous } from '../../../../gen-src/icons/SchemaViewerDateTimeContinuous';
import { SvgSchemaViewerDateTimeDiscrete } from '../../../../gen-src/icons/SchemaViewerDateTimeDiscrete';
import { SvgSchemaViewerGeoCalcContinuous } from '../../../../gen-src/icons/SchemaViewerGeoCalcContinuous';
import { SvgSchemaViewerGeoCalcDiscrete } from '../../../../gen-src/icons/SchemaViewerGeoCalcDiscrete';
import { SvgSchemaViewerGeoContinuous } from '../../../../gen-src/icons/SchemaViewerGeoContinuous';
import { SvgSchemaViewerGeoDiscrete } from '../../../../gen-src/icons/SchemaViewerGeoDiscrete';
import { SvgSchemaViewerGroupDiscrete } from '../../../../gen-src/icons/SchemaViewerGroupDiscrete';
import { SvgSchemaViewerGroupGeoDiscrete } from '../../../../gen-src/icons/SchemaViewerGroupGeoDiscrete';
import { SvgSchemaViewerHierarchy } from '../../../../gen-src/icons/SchemaViewerHierarchy';
import { SvgSchemaViewerMeasuresNumberCalcContinuous } from '../../../../gen-src/icons/SchemaViewerMeasuresNumberCalcContinuous';
import { SvgSchemaViewerNumberCalcDiscrete } from '../../../../gen-src/icons/SchemaViewerNumberCalcDiscrete';
import { SvgSchemaViewerNumberContinuous } from '../../../../gen-src/icons/SchemaViewerNumberContinuous';
import { SvgSchemaViewerNumberDiscrete } from '../../../../gen-src/icons/SchemaViewerNumberDiscrete';
import { SvgSchemaViewerNumericBin } from '../../../../gen-src/icons/SchemaViewerNumericBin';
import { SvgSchemaViewerNumericBinContinuous } from '../../../../gen-src/icons/SchemaViewerNumericBinContinuous';
import { SvgSchemaViewerSet } from '../../../../gen-src/icons/SchemaViewerSet';
import { SvgSchemaViewerTextCalcContinuous } from '../../../../gen-src/icons/SchemaViewerTextCalcContinuous';
import { SvgSchemaViewerTextCalcDiscrete } from '../../../../gen-src/icons/SchemaViewerTextCalcDiscrete';
import { SvgSchemaViewerTextContinuous } from '../../../../gen-src/icons/SchemaViewerTextContinuous';
import { SvgSchemaViewerTextDiscrete } from '../../../../gen-src/icons/SchemaViewerTextDiscrete';
import { SvgSchemaViewerUnknown } from '../../../../gen-src/icons/SchemaViewerUnknown';
import { FieldIcon } from '../types/FieldIcon';

export function SvgFieldIcon(props: { fieldIcon: FieldIcon | null }): JSX.Element | null {
  if (!props.fieldIcon) {
    return <SvgSchemaViewerUnknown />;
  }
  switch (props.fieldIcon) {

    // Booleans are always discrete
    case FieldIcon.BoolCalc:
      return <SvgSchemaViewerBoolCalcDiscrete />;
    case FieldIcon.Bool:
      return <SvgSchemaViewerBoolDiscrete />;

    case FieldIcon.DateCalcContinuous:
      return <SvgSchemaViewerDateCalcContinuous />;
    case FieldIcon.DateCalcDiscrete:
      return <SvgSchemaViewerDateCalcDiscrete />;
    case FieldIcon.DateContinuous:
      return <SvgSchemaViewerDateContinuous />;
    case FieldIcon.DateDiscrete:
      return <SvgSchemaViewerDateDiscrete />;

    case FieldIcon.DateTimeCalcContinuous:
      return <SvgSchemaViewerDateTimeCalcContinuous />;
    case FieldIcon.DateTimeCalcDiscrete:
      return <SvgSchemaViewerDateTimeCalcDiscrete />;
    case FieldIcon.DateTimeContinuous:
      return <SvgSchemaViewerDateTimeContinuous />;
    case FieldIcon.DateTimeDiscrete:
      return <SvgSchemaViewerDateTimeDiscrete />;

    case FieldIcon.GeoCalcContinuous:
      return <SvgSchemaViewerGeoCalcContinuous />;
    case FieldIcon.GeoCalcDiscrete:
      return <SvgSchemaViewerGeoCalcDiscrete />;
    case FieldIcon.GeoContinuous:
      return <SvgSchemaViewerGeoContinuous />;
    case FieldIcon.GeoDiscrete:
      return <SvgSchemaViewerGeoDiscrete />;

    // Groups are always discrete
    case FieldIcon.Group:
      return <SvgSchemaViewerGroupDiscrete />;
    case FieldIcon.GroupGeo:
      return <SvgSchemaViewerGroupGeoDiscrete />;

    case FieldIcon.Hierarchy:
      return <SvgSchemaViewerHierarchy />;

    case FieldIcon.NumberCalcContinuous:
      return <SvgSchemaViewerMeasuresNumberCalcContinuous />; // This icon has an inconsistent name
    case FieldIcon.NumberCalcDiscrete:
      return <SvgSchemaViewerNumberCalcDiscrete />;
    case FieldIcon.NumberContinuous:
      return <SvgSchemaViewerNumberContinuous />;
    case FieldIcon.NumberDiscrete:
      return <SvgSchemaViewerNumberDiscrete />;

    case FieldIcon.NumericBinContinuous:
      return <SvgSchemaViewerNumericBinContinuous />;
    case FieldIcon.NumericBinDiscrete:
      return <SvgSchemaViewerNumericBin />; // This discrete icon has no suffix (unlike the others)

    case FieldIcon.Set:
      return <SvgSchemaViewerSet />;

    case FieldIcon.TextCalcContinuous:
      return <SvgSchemaViewerTextCalcContinuous />;
    case FieldIcon.TextCalcDiscrete:
      return <SvgSchemaViewerTextCalcDiscrete />;
    case FieldIcon.TextContinuous:
      return <SvgSchemaViewerTextContinuous />;
    case FieldIcon.TextDiscrete:
      return <SvgSchemaViewerTextDiscrete />;

    case FieldIcon.Unknown:
      return <SvgSchemaViewerUnknown />;

    default:
      // This is to cause a compile-time error if a case is missing.
      assertNever(props.fieldIcon);
      return null; // This code is unreachable but requred by the complier.
  }
}
